/**
 * Edit Bill Line Page
 *
 * Dedicated page for editing a line item in a bill.
 */

import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, Loader2, ShieldCheck, Ban } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useToast } from '@/components/ui/toaster'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'
import {
  useApprovalRequests,
  useCancelRequest,
  useMarkApprovalsSeen,
  useRequestDiscount,
} from '@/hooks/useApprovals'
import { NoteDialog } from '@/components/approvals/NoteDialog'
import { parseApiError } from '@/lib/utils'

interface BillLine {
  id: number
  article_id: number
  article_code: string
  article_description: string
  quantity: number
  unit_price: number
  discount_percent: number
  subtotal: number
  tax_rate: number
  total: number
}

interface Bill {
  id: number
  status: string
  lines: BillLine[]
}

export function EditLinePage() {
  const { id, lineId } = useParams<{ id: string; lineId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { isSeller } = useAuth()

  // Fetch bill data
  const { data: bill, isLoading } = useQuery({
    queryKey: ['bill', id],
    queryFn: async () => {
      const response = await api.get(`/bills/${id}`)
      return response.data as Bill
    },
    enabled: !!id,
  })

  // Find the line
  const line = bill?.lines.find(l => l.id === parseInt(lineId || '0'))

  // Solicitudes del vendedor para esta línea (la más nueva primero)
  const { data: lineRequests = [] } = useApprovalRequests(
    { billId: Number(id), kind: 'discount' },
    { enabled: isSeller && !!id, poll: true }
  )
  const latestRequest = lineRequests.find((r) => r.line_id === Number(lineId))
  const openRequest = latestRequest?.status === 'pending' ? latestRequest : undefined
  const requestDiscount = useRequestDiscount()
  const cancelRequest = useCancelRequest()
  const [askNote, setAskNote] = useState(false)
  const [askCancel, setAskCancel] = useState(false)

  // La respuesta del admin (aprobada o rechazada) se muestra una sola vez: se
  // guarda para esta visita y se marca vista, así no queda como aviso viejo.
  const markSeen = useMarkApprovalsSeen()
  const [answer, setAnswer] = useState<typeof latestRequest | null>(null)
  useEffect(() => {
    if (
      latestRequest?.unseen &&
      (latestRequest.status === 'applied' || latestRequest.status === 'rejected') &&
      answer?.id !== latestRequest.id
    ) {
      setAnswer(latestRequest)
      markSeen.mutate({ ids: [latestRequest.id] })
    }
    // markSeen cambia en cada render; solo importa la solicitud
  }, [latestRequest?.id, latestRequest?.unseen, latestRequest?.status])

  // Form state
  const [quantity, setQuantity] = useState(1)
  const [unitPrice, setUnitPrice] = useState(0)
  // Se edita como texto: con un número, borrar el campo volvía a poner 0.
  const [discountText, setDiscountText] = useState('')
  const discountTooHigh = Number(discountText.replace(',', '.')) > 100
  const discountPercent = Math.min(100, Math.max(0, Number(discountText.replace(',', '.')) || 0))
  const setDiscountPercent = (value: number) => setDiscountText(value ? String(value) : '')

  // Se carga una vez por línea: una recarga de la factura no pisa lo que se está escribiendo.
  const initializedLineId = useRef<number | null>(null)
  useEffect(() => {
    if (line && initializedLineId.current !== line.id) {
      initializedLineId.current = line.id
      setQuantity(line.quantity)
      setUnitPrice(line.unit_price)
      setDiscountPercent(line.discount_percent)
    }
  }, [line])

  // Solo cuando la solicitud abierta pasa a aplicada (no por una vieja): el
  // descuento aprobado ya está en la línea y se pone en el campo.
  const watchedRequest = useRef<{ id: number; status: string } | null>(null)
  useEffect(() => {
    const previous = watchedRequest.current
    watchedRequest.current = latestRequest ? { id: latestRequest.id, status: latestRequest.status } : null
    if (
      latestRequest?.status === 'applied' &&
      previous?.id === latestRequest.id &&
      previous.status === 'pending'
    ) {
      setDiscountPercent(latestRequest.discount_percent ?? 0)
      queryClient.invalidateQueries({ queryKey: ['bill', id] })
    }
  }, [latestRequest, id, queryClient])

  // Calculate estimated total using the article's real IVA rate (from the
  // line) instead of hardcoding 13%, so exempt items (0%) and reduced rates
  // (1/2/4%) preview correctly. The authoritative recalculation happens
  // server-side on save.
  const calculateEstimatedTotal = () => {
    const grossSubtotal = quantity * unitPrice
    const discount = grossSubtotal * (discountPercent / 100)
    const afterDiscount = grossSubtotal - discount
    const tax = afterDiscount * ((line?.tax_rate ?? 0) / 100)
    return afterDiscount + tax
  }

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/bills/${id}/lines/${lineId}`, {
        quantity,
        unit_price: unitPrice,
        discount_percent: discountPercent,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill', id] })
      toast({ title: t('pos.lineUpdated'), variant: 'success' })
      navigate(`/bills/new?continue=${id}`)
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('pos.updateError'),
        variant: 'destructive',
      })
    },
  })

  // El vendedor no sube el descuento: lo pide. Puede bajarlo o quitarlo.
  const needsApproval = isSeller && !!line && discountPercent > (line.discount_percent || 0)

  const handleSubmit = () => {
    if (needsApproval) {
      setAskNote(true)
      return
    }
    updateMutation.mutate()
  }

  const sendDiscountRequest = async (note: string) => {
    if (!line) return
    try {
      // La cantidad se guarda ya; el descuento queda esperando la aprobación.
      if (quantity !== line.quantity) {
        await api.put(`/bills/${id}/lines/${lineId}`, {
          quantity,
          unit_price: unitPrice,
          discount_percent: line.discount_percent,
        })
        queryClient.invalidateQueries({ queryKey: ['bill', id] })
      }
      await requestDiscount.mutateAsync({
        bill_id: Number(id),
        line_id: line.id,
        discount_percent: discountPercent,
        note: note || undefined,
      })
      setAskNote(false)
      // El campo vuelve a lo que de verdad tiene la línea hasta que el admin apruebe.
      setDiscountPercent(line.discount_percent)
      toast({ title: t('approvals.requestSent'), variant: 'success' })
    } catch (error) {
      toast({ title: t('common.error'), description: parseApiError(error), variant: 'destructive' })
    }
  }

  const confirmCancel = async (note: string) => {
    if (!openRequest) return
    try {
      await cancelRequest.mutateAsync({ id: openRequest.id, note })
      setAskCancel(false)
      toast({ title: t('approvals.cancelled') })
    } catch (error) {
      toast({ title: t('common.error'), description: parseApiError(error), variant: 'destructive' })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!bill || !line) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">{t('pos.lineNotFound')}</p>
        <Button variant="outline" onClick={() => navigate(`/bills/new?continue=${id}`)} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    )
  }

  // Redirect if bill is finalized
  if (bill.status === 'finalized') {
    navigate(`/bills/${id}`)
    return null
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/bills/new?continue=${id}`)}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold">
            {t('pos.editLine')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {line.article_code}
          </p>
        </div>
      </div>

      {/* Article Info */}
      <Card>
        <CardHeader>
          <CardTitle>{line.article_code}</CardTitle>
          <CardDescription>{line.article_description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quantity */}
          <div className="space-y-2">
            <Label>{t('pos.quantity')}</Label>
            <Input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="h-14 text-xl text-center"
            />
          </div>

          {/* Unit Price (read-only — el precio no se edita desde aquí) */}
          <div className="space-y-2">
            <Label>{t('pos.unitPrice')}</Label>
            <div className="h-14 flex items-center justify-center rounded-md border border-input bg-muted text-xl font-medium">
              {formatCurrency(unitPrice)}
            </div>
          </div>

          {/* Discount */}
          <div className="space-y-2">
            <Label>{t('pos.discountPercent')}</Label>
            <Input
              type="text"
              inputMode={isSeller ? 'numeric' : 'decimal'}
              placeholder="0"
              value={discountText}
              onChange={(e) => {
                // El vendedor pide descuentos enteros (la columna es entera); el admin puede usar decimales.
                const pattern = isSeller ? /^\d{0,3}$/ : /^\d{0,3}([.,]\d{0,2})?$/
                const text = e.target.value.trim()
                if (pattern.test(text)) setDiscountText(text)
              }}
              // Al tocarlo se selecciona: escribir reemplaza el valor en vez de sumarle dígitos.
              onFocus={(e) => e.target.select()}
              aria-invalid={discountTooHigh}
              className="h-14 text-xl text-center"
            />
            {discountTooHigh && (
              <p className="text-sm text-destructive" role="alert">
                {t('approvals.discountMax')}
              </p>
            )}
            {needsApproval && !discountTooHigh && (
              <p className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  {t('approvals.discountNeedsApproval')}. {t('approvals.discountNeedsApprovalHelp')}
                </span>
              </p>
            )}
          </div>

          {/* Solicitud abierta, esperando al admin */}
          {openRequest && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-3">
              <p className="font-medium">
                {t('approvals.waitingApproval', { percent: openRequest.discount_percent ?? 0 })}
              </p>
              {needsApproval && discountPercent !== (openRequest.discount_percent ?? 0) && (
                <p className="text-sm">
                  {t('approvals.replacesRequest', { percent: openRequest.discount_percent ?? 0 })}
                </p>
              )}
              <Button variant="ghost" className="h-10 px-2" onClick={() => setAskCancel(true)}>
                <Ban className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('approvals.cancel')}
              </Button>
            </div>
          )}
          {!openRequest && answer?.status === 'rejected' && (
            <div className="rounded-lg border border-destructive/40 p-4 text-sm">
              <p className="font-medium text-destructive">
                {t('approvals.rejectedNotice', { percent: answer.discount_percent ?? 0 })}
              </p>
              {answer.resolution_note && <p>“{answer.resolution_note}”</p>}
            </div>
          )}
          {!openRequest && answer?.status === 'applied' && (
            <div className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/30 p-4 text-sm">
              <p className="font-medium text-green-700 dark:text-green-400">
                {t('approvals.approvedNotice', { percent: answer.discount_percent ?? 0 })}
              </p>
              {answer.resolution_note && <p>“{answer.resolution_note}”</p>}
            </div>
          )}

          {/* Estimated Total */}
          <div className="p-4 bg-primary/10 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('pos.estimatedTotal')}:</span>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(calculateEstimatedTotal())}
              </span>
            </div>
            {discountPercent > 0 && !needsApproval && (
              <p className="text-sm text-green-600 text-right mt-1">
                {t('approvals.discountShown', { percent: discountPercent })}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Fixed Footer Actions */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-background border-t z-50 md:relative md:bottom-0 md:border-0 md:p-0 md:bg-transparent md:z-auto">
        <div className="flex gap-2 max-w-screen-lg mx-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/bills/new?continue=${id}`)}
            className="flex-1 h-12"
          >
            {t('pos.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={updateMutation.isPending || discountTooHigh}
            className="flex-1 h-12"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : needsApproval ? (
              <ShieldCheck className="h-4 w-4 mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {needsApproval ? t('approvals.requestDiscount') : t('pos.save')}
          </Button>
        </div>
      </div>

      <NoteDialog
        open={askNote}
        onOpenChange={setAskNote}
        title={t('approvals.requestDiscountTitle')}
        description={t('approvals.discountOf', { percent: discountPercent })}
        confirmLabel={t('approvals.send')}
        isPending={requestDiscount.isPending}
        onConfirm={sendDiscountRequest}
      />
      <NoteDialog
        open={askCancel}
        onOpenChange={setAskCancel}
        title={t('approvals.cancelTitle')}
        confirmLabel={t('approvals.confirmCancel')}
        required
        destructive
        isPending={cancelRequest.isPending}
        onConfirm={confirmCancel}
      />
    </div>
  )
}
